import { useState, useEffect } from 'react';
import { apiFetch } from '../lib/api.js';

interface ActivityItem {
  id: string;
  action: string;
  category: string;
  description: string | null;
  userEmail: string | null;
  entityType: string | null;
  entityName: string | null;
  changes: Record<string, { before: unknown; after: unknown }> | null;
  createdAt: string;
}

const CATEGORY_COLORS: Record<string, string> = {
  auth: 'bg-blue-500',
  entity: 'bg-green-500',
  user_mgmt: 'bg-purple-500',
  integration: 'bg-orange-500',
  config: 'bg-yellow-500',
  compliance: 'bg-red-500',
  system: 'bg-gray-500',
  agent: 'bg-teal-500',
};

const ACTION_LABELS: Record<string, string> = {
  'auth.login': 'Logged in',
  'auth.login_failed': 'Login failed',
  'auth.logout': 'Logged out',
  'auth.password_reset': 'Password reset',
  'entity.lead_created': 'Lead captured',
  'entity.lead_exported': 'Leads exported',
  'entity.org_created': 'Account created',
  'entity.org_updated': 'Account updated',
  'entity.org_transition': 'Status changed',
  'entity.business_created': 'Business added',
  'entity.business_updated': 'Business updated',
  'entity.location_created': 'Location added',
  'entity.location_updated': 'Location updated',
  'config.notification_updated': 'Notifications updated',
  'config.settings_updated': 'Settings updated',
  'config.feature_flag_set': 'Feature flag changed',
  'config.drip_sequence_created': 'Drip sequence created',
  'integration.created': 'Integration connected',
  'integration.removed': 'Integration removed',
  'compliance.opt_out': 'Unsubscribed',
  'compliance.data_export': 'Data exported',
  'compliance.data_erasure': 'Data erased',
  'system.notification_sent': 'Notification sent',
  'system.notification_failed': 'Notification failed',
  'agent.task_triggered': 'Agent task triggered',
  'agent.webhook_delivered': 'Agent webhook delivered',
  'agent.webhook_failed': 'Agent webhook failed',
  'agent.site_deployed': 'Website deployed',
  'agent.site_deploy_failed': 'Website deploy failed',
  'agent.site_edit_requested': 'Website edit requested',
  'agent.site_edit_confirmed': 'Website edit confirmed',
  'agent.site_config_changed': 'Website content updated',
  'agent.notification_sent': 'Drip email sent',
  'agent.notification_failed': 'Notification failed',
  'agent.drip_step_sent': 'Drip step sent',
  'agent.event_dead_lettered': 'Event dead-lettered',
};

function timeAgo(dateStr: string): string {
  const seconds = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  const days = Math.floor(seconds / 86400);
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

interface ActivityFeedProps {
  /** Filter by entity type + id (e.g., for lead detail) */
  entityType?: string;
  entityId?: string;
  /** Filter by org (for admin view) */
  orgId?: string;
  /** Max items to show */
  limit?: number;
  /** Title override */
  title?: string;
}

const CATEGORY_LABELS: Record<string, string> = {
  auth: 'Auth', entity: 'Entities', user_mgmt: 'Users', integration: 'Integrations',
  config: 'Config', compliance: 'Compliance', system: 'System', agent: 'Agent',
};

export function ActivityFeed({ entityType, entityId, orgId, limit = 20, title = 'Activity' }: ActivityFeedProps) {
  const [items, setItems] = useState<ActivityItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null);

  useEffect(() => {
    setIsLoading(true);

    let path: string;
    if (entityType && entityId) {
      path = `/activity/entity/${entityType}/${entityId}?limit=${limit}`;
    } else {
      const params = new URLSearchParams({ limit: String(limit) });
      if (orgId) params.set('org_id', orgId);
      path = `/activity?${params}`;
    }

    apiFetch<{ activity: ActivityItem[] }>(path)
      .then(data => setItems(data.activity))
      .catch(() => setItems([]))
      .finally(() => setIsLoading(false));
  }, [entityType, entityId, orgId, limit]);

  const filteredItems = categoryFilter ? items.filter(i => i.category === categoryFilter) : items;
  const categoryCounts = items.reduce((acc, i) => { acc[i.category] = (acc[i.category] ?? 0) + 1; return acc; }, {} as Record<string, number>);

  if (isLoading) {
    return (
      <div className="bg-white rounded-xl border border-gray-100 p-4">
        <h3 className="text-sm font-medium text-gray-700 mb-3">{title}</h3>
        <div className="space-y-3">
          {[1, 2, 3].map(i => (
            <div key={i} className="flex gap-3 animate-pulse">
              <div className="w-2 h-2 bg-gray-200 rounded-full mt-2" />
              <div className="flex-1"><div className="h-3 bg-gray-200 rounded w-3/4 mb-1" /><div className="h-2 bg-gray-100 rounded w-1/2" /></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-gray-100 p-4">
        <h3 className="text-sm font-medium text-gray-700 mb-2">{title}</h3>
        <p className="text-xs text-gray-400">No activity recorded yet.</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-100 p-4">
      <h3 className="text-sm font-medium text-gray-700 mb-2">{title}</h3>

      {/* Category filter pills */}
      {Object.keys(categoryCounts).length > 1 && (
        <div className="flex flex-wrap gap-1 mb-3">
          <button
            onClick={() => setCategoryFilter(null)}
            className={`px-2 py-0.5 text-xs rounded-full transition-colors ${
              !categoryFilter ? 'bg-gray-800 text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
            }`}
          >
            All
          </button>
          {Object.entries(categoryCounts).sort(([a], [b]) => a.localeCompare(b)).map(([cat, count]) => (
            <button
              key={cat}
              onClick={() => setCategoryFilter(categoryFilter === cat ? null : cat)}
              className={`px-2 py-0.5 text-xs rounded-full transition-colors flex items-center gap-1 ${
                categoryFilter === cat ? 'bg-gray-800 text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
              }`}
            >
              <span className={`w-1.5 h-1.5 rounded-full ${CATEGORY_COLORS[cat] ?? 'bg-gray-400'}`} />
              {CATEGORY_LABELS[cat] ?? cat} <span className="opacity-60">{count}</span>
            </button>
          ))}
        </div>
      )}

      <div className="relative">
        {/* Timeline line */}
        <div className="absolute left-[5px] top-1 bottom-1 w-px bg-gray-200" />

        <div className="space-y-3">
          {filteredItems.map(item => {
            const label = ACTION_LABELS[item.action] ?? item.action.split('.').pop()?.replace(/_/g, ' ') ?? item.action;
            const color = CATEGORY_COLORS[item.category] ?? 'bg-gray-400';
            const hasChanges = item.changes && Object.keys(item.changes).length > 0;

            return (
              <div key={item.id} className="flex gap-3 relative">
                <div className={`w-2.5 h-2.5 ${color} rounded-full mt-1.5 flex-shrink-0 ring-2 ring-white`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <button
                      onClick={() => hasChanges ? setExpandedId(expandedId === item.id ? null : item.id) : null}
                      className={`text-sm text-gray-900 text-left ${hasChanges ? 'hover:text-primary-600 cursor-pointer' : ''}`}
                    >
                      {label}
                      {item.entityName && <span className="text-gray-500"> — {item.entityName}</span>}
                      {hasChanges && <span className="text-xs text-gray-400 ml-1">(details)</span>}
                    </button>
                    <span className="text-xs text-gray-400 flex-shrink-0 ml-2">{timeAgo(item.createdAt)}</span>
                  </div>
                  {item.userEmail && <p className="text-xs text-gray-400">{item.userEmail}</p>}

                  {/* Expanded diff view */}
                  {expandedId === item.id && hasChanges && (
                    <div className="mt-2 bg-gray-50 rounded-lg p-2 text-xs space-y-1">
                      {Object.entries(item.changes!).map(([field, { before, after }]) => (
                        <div key={field} className="flex items-start gap-2">
                          <span className="text-gray-500 font-medium w-24 flex-shrink-0">{field}:</span>
                          <span className="text-red-500 line-through">{String(before ?? '—')}</span>
                          <span className="text-gray-400">&rarr;</span>
                          <span className="text-green-600">{String(after ?? '—')}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
